import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

// Written to by the ecomm lambda via @workd/pd-model and read here via TypeORM.
// Column names follow the modern (CLAUDE.md) convention: no leading underscore
// on the fk_* columns.
//
// The geo* columns are populated at write time from the active client's
// shipping address so they are a snapshot of the client's location at the time
// of the search. Anonymous searches have null geo. The ipAddress column is
// captured as a future hook for IP-based geocoding / abuse detection but is
// not currently used by the report.
@Index('idx_item_search_log_searched_at', ['searchedAt'], {})
@Index('idx_item_search_log_client_date', ['clientId', 'searchedAt'], {})
@Index('idx_item_search_log_normalized', ['normalizedSearchText'], {})
@Index('idx_item_search_log_session', ['searchSessionId'], {})
@Index(
	'idx_item_search_log_public_user_date',
	['publicUserId', 'searchedAt'],
	{},
)
@Entity('item_search_log', {
	comment:
		'One row per item-search request from the ecommerce storefront. Written fire-and-forget by the ecomm lambda; consumed by the CRM Item Search Activity report.',
})
export class ItemSearchLogEntity extends BaseEntity {
	@Column('varchar', {
		name: 'searchText',
		length: 500,
		comment: 'Raw search text as typed by the user.',
	})
	public searchText!: string;

	@Column('varchar', {
		name: 'normalizedSearchText',
		length: 500,
		comment:
			'Search text lowercased and whitespace-collapsed; used as the grouping key in the report.',
	})
	public normalizedSearchText!: string;

	@Column('int', {
		name: 'fk_client__id',
		unsigned: true,
		nullable: true,
		comment:
			'Active client at the moment of the search. Null for anonymous storefront visitors.',
	})
	public clientId!: number | null;

	@Column('int', {
		name: 'fk_public_user__id',
		unsigned: true,
		nullable: true,
		comment:
			'Authenticated storefront user who performed the search. Null for anonymous visitors.',
	})
	public publicUserId!: number | null;

	@Column('int', {
		name: 'workdUserId',
		unsigned: true,
		nullable: true,
		comment:
			'Workd user ID from the JWT, kept as a fallback identifier. Null for the anonymous public-user sentinel.',
	})
	public workdUserId!: number | null;

	@Column('varchar', {
		name: 'searchSessionId',
		length: 36,
		comment:
			'Per-tab UUID from the X-Search-Session-Id header. Lets the report collapse rapid-fire keystrokes ("a" → "ac" → "acetaminophen") into one logical search.',
	})
	public searchSessionId!: string;

	@Column('int', {
		name: 'resultCount',
		nullable: true,
		comment:
			'Number of items the search returned; used to surface zero-result searches in the report.',
	})
	public resultCount!: number | null;

	@Column('json', {
		name: 'filterContext',
		nullable: true,
		comment:
			'The non-searchText portion of the ObjectQueryModel filter (category, manufacturer, isInStock, etc.). Useful for "were they refining or starting fresh?" analysis.',
	})
	public filterContext!: unknown | null;

	@Column('varchar', {
		name: 'ipAddress',
		length: 45,
		nullable: true,
		comment:
			'Client IP captured at the lambda. Stored for future use (e.g. IP-based geocoding, abuse detection). Not currently used to populate the geo* fields.',
	})
	public ipAddress!: string | null;

	@Column('text', {
		name: 'userAgent',
		nullable: true,
		comment: 'Raw User-Agent header at the time of the search.',
	})
	public userAgent!: string | null;

	@Column('varchar', {
		name: 'geoCountry',
		length: 64,
		nullable: true,
		comment:
			'Country of the search. Currently unused because client_address has no country field; reserved for a future IP-geocoding path that populates it alongside geoRegion / geoCity.',
	})
	public geoCountry!: string | null;

	@Column('varchar', {
		name: 'geoRegion',
		length: 64,
		nullable: true,
		comment:
			"State / province snapshot from the active client's shipping address at the moment of the search. Null for anonymous searches.",
	})
	public geoRegion!: string | null;

	@Column('varchar', {
		name: 'geoCity',
		length: 128,
		nullable: true,
		comment:
			"City snapshot from the active client's shipping address at the moment of the search. Null for anonymous searches.",
	})
	public geoCity!: string | null;

	@Column('datetime', {
		name: 'searchedAt',
		default: () => 'CURRENT_TIMESTAMP',
		comment:
			'When the user actually hit the lambda. Distinct from _created_date, which is when the row was persisted (after the fire-and-forget delay).',
	})
	public searchedAt!: Date;
}
